import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

interface ExcelFilterProps {
  column: string;
  values: string[];
  selectedValues: string[];
  onFilterChange: (column: string, selectedValues: string[]) => void;
  isDarkTheme?: boolean;
}

const ExcelFilter: React.FC<ExcelFilterProps> = ({
  column,
  values,
  selectedValues,
  onFilterChange,
  isDarkTheme = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const uniqueValues = Array.from(new Set(values.filter(v => v && v.trim() !== ''))).sort();
  const filteredValues = uniqueValues.filter(value =>
    value.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isAllSelected = selectedValues.length === uniqueValues.length;
  const isIndeterminate = selectedValues.length > 0 && selectedValues.length < uniqueValues.length;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInButton = buttonRef.current && buttonRef.current.contains(target);
      const isClickInDropdown = dropdownRef.current && dropdownRef.current.contains(target);
      
      if (!isClickInButton && !isClickInDropdown) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelectAll = () => {
    if (isAllSelected) {
      onFilterChange(column, []);
    } else {
      onFilterChange(column, uniqueValues);
    }
  };

  const handleValueToggle = (value: string) => {
    const newSelected = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onFilterChange(column, newSelected);
  };

  const hasActiveFilter = selectedValues.length > 0 && selectedValues.length < uniqueValues.length;

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }} ref={dropdownRef}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '2px 4px',
          marginLeft: '4px',
          color: hasActiveFilter ? '#007bff' : (isDarkTheme ? '#ccc' : '#666'),
          fontSize: '12px'
        }}
        title={`Filter ${column}`}
      >
        ▼
      </button>

      {isOpen && createPortal(
        <div 
          ref={dropdownRef}
          style={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            backgroundColor: isDarkTheme ? '#333' : '#fff',
            border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
            borderRadius: '4px',
            zIndex: 99999,
            width: '200px',
            maxHeight: '300px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            padding: '8px'
          }}>
          {/* Search box */}
          <input
            type="text"
            placeholder="Search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '4px 8px',
              border: `1px solid ${isDarkTheme ? '#555' : '#ccc'}`,
              borderRadius: '4px',
              backgroundColor: isDarkTheme ? '#444' : '#fff',
              color: isDarkTheme ? '#fff' : '#000',
              fontSize: '12px',
              marginBottom: '8px',
              boxSizing: 'border-box'
            }}
          />

          {/* Select All checkbox */}
          <div style={{
            padding: '4px 0',
            borderBottom: `1px solid ${isDarkTheme ? '#555' : '#eee'}`,
            marginBottom: '4px'
          }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '12px',
              color: isDarkTheme ? '#fff' : '#000'
            }}>
              <input
                type="checkbox"
                checked={isAllSelected}
                ref={(input) => {
                  if (input) input.indeterminate = isIndeterminate;
                }}
                onChange={handleSelectAll}
                style={{ marginRight: '6px' }}
              />
              Select All
            </label>
          </div>

          {/* Values list */}
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {filteredValues.map((value) => (
              <div key={value} style={{ padding: '2px 0' }}>
                <label style={{
                  display: 'flex',
                  alignItems: 'center',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: isDarkTheme ? '#fff' : '#000'
                }}>
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(value)}
                    onChange={() => handleValueToggle(value)}
                    style={{ marginRight: '6px' }}
                  />
                  {value}
                </label>
              </div>
            ))}
          </div>

          {filteredValues.length === 0 && (
            <div style={{
              padding: '8px',
              color: isDarkTheme ? '#ccc' : '#666',
              fontSize: '12px',
              textAlign: 'center'
            }}>
              No items found
            </div>
          )}
        </div>,
        document.body
      )}
    </div>
  );
};

export default ExcelFilter;